import type { CoverageDimensionScore } from './coverageDimensions';
import { normalizeCoverageDimensionScores } from './coverageDimensions';
import {
  computeReadinessScores,
  getWeakestCoverageDimensions,
  type ReadinessScores,
} from './coverageScoring';
import { shouldSuggestQuestions } from './suggestQuestions';
import type { ImplementationElaborationResponse } from '../schemas/implCheck';
import {
  shouldAllowAutoCoverageGapLoop,
  type ReadinessPhase,
} from './requirementReadinessLifecycle';

/** Goal threshold for coverage completeness. */
export const COVERAGE_GOAL_THRESHOLD = 80;

/** Minimum ticket readiness required to send a requirement to Linear. */
export const TICKET_SEND_THRESHOLD = 70;

/** Minimum cursor readiness score required to send a requirement to Cursor. */
export const CURSOR_SEND_THRESHOLD = 70;

/** Auto-suggest questions only while graph score (coverage completeness) is at or below this value. */
const AUTO_SUGGEST_GRAPH_THRESHOLD = 75;

/** Graph score (coverage completeness) at or above which a requirement counts as resolved on the dashboard. */
export const GRAPH_RESOLUTION_THRESHOLD = 70;

/** Per-dimension floor below which gap questions should target that dimension. */
const COVERAGE_GAP_DIMENSION_THRESHOLD = 70;

/** Hard stop to prevent runaway LLM loops per requirement. */
export const MAX_COVERAGE_GAP_PASSES = 10;

export interface CoverageGoalReadiness {
  coverageCompleteness: number;
  ticketReadiness: number;
}

export interface ShouldTriggerCoverageGapQuestionsOpts {
  coverageDimensions: CoverageDimensionScore[];
  readiness: CoverageGoalReadiness;
  readinessPhase?: ReadinessPhase;
  implStatus?: string | null;
  elaboration?: ImplementationElaborationResponse | null;
  pendingSuggestionCount: number;
  gapPassCount: number;
  lastGapFingerprint?: string | null;
  currentGapFingerprint: string;
}

export function isCoverageGoalMet(readiness: CoverageGoalReadiness): boolean {
  return (
    readiness.coverageCompleteness >= COVERAGE_GOAL_THRESHOLD
    && readiness.ticketReadiness >= COVERAGE_GOAL_THRESHOLD
  );
}

export function isGraphResolutionThresholdMet(coverageCompleteness: number): boolean {
  return coverageCompleteness >= GRAPH_RESOLUTION_THRESHOLD;
}

export function isBelowCoverageGoal(readiness: CoverageGoalReadiness): boolean {
  return !isCoverageGoalMet(readiness);
}

export function isGraphScoreEligibleForAutoSuggest(coverageCompleteness: number): boolean {
  return coverageCompleteness <= AUTO_SUGGEST_GRAPH_THRESHOLD;
}

function isEligibleForAutoCoverageGapQuestions(readiness: CoverageGoalReadiness): boolean {
  return (
    isGraphScoreEligibleForAutoSuggest(readiness.coverageCompleteness)
    || readiness.ticketReadiness < TICKET_SEND_THRESHOLD
  );
}

export function shouldAutoSuggestQuestionsForRequirement(
  req: {
    completeness: number;
    coverageDimensions?: CoverageDimensionScore[];
    readinessPhase?: ReadinessPhase;
  },
): boolean {
  const readiness = req.coverageDimensions && req.coverageDimensions.length > 0
    ? computeReadinessScores(req.coverageDimensions)
    : null;

  if (
    req.readinessPhase
    && !shouldAllowAutoCoverageGapLoop(req.readinessPhase, readiness?.ticketReadiness)
  ) {
    return false;
  }

  if (readiness) {
    return isEligibleForAutoCoverageGapQuestions({
      coverageCompleteness: readiness.coverageCompleteness,
      ticketReadiness: readiness.ticketReadiness,
    });
  }
  if (req.completeness > 0) {
    return isGraphScoreEligibleForAutoSuggest(req.completeness);
  }
  return true;
}

export function buildCoverageGapFingerprint(
  dimensions: CoverageDimensionScore[],
  readiness: CoverageGoalReadiness,
): string {
  const sorted = [...dimensions]
    .sort((a, b) => a.code.localeCompare(b.code))
    .map(d => `${d.code}:${d.score}`)
    .join('|');
  return `${readiness.coverageCompleteness}|${readiness.ticketReadiness}|${sorted}`;
}

export function getCoverageGapTargets(
  dimensions: CoverageDimensionScore[],
  threshold = COVERAGE_GAP_DIMENSION_THRESHOLD,
): CoverageDimensionScore[] {
  const normalized = normalizeCoverageDimensionScores(dimensions);
  const belowThreshold = normalized.filter(d => d.score < threshold);
  if (belowThreshold.length > 0) {
    return [...belowThreshold].sort((a, b) => a.score - b.score).slice(0, 3);
  }
  return getWeakestCoverageDimensions(normalized, 3);
}

export function shouldTriggerCoverageGapQuestions(
  opts: ShouldTriggerCoverageGapQuestionsOpts,
): boolean {
  if (opts.coverageDimensions.length === 0) {
    return false;
  }

  if (
    opts.readinessPhase
    && !shouldAllowAutoCoverageGapLoop(opts.readinessPhase, opts.readiness.ticketReadiness)
  ) {
    return false;
  }

  if (!isEligibleForAutoCoverageGapQuestions(opts.readiness)) {
    return false;
  }

  if (!shouldSuggestQuestions({
    implStatus: opts.implStatus,
    elaboration: opts.elaboration,
  })) {
    return false;
  }

  if (opts.pendingSuggestionCount > 0) {
    return false;
  }

  if (opts.gapPassCount >= MAX_COVERAGE_GAP_PASSES) {
    return false;
  }

  if (
    opts.lastGapFingerprint != null
    && opts.lastGapFingerprint === opts.currentGapFingerprint
  ) {
    return false;
  }

  return true;
}

function isRequirementBelowCoverageGoal(
  req: {
    completeness: number;
    coverageDimensions?: CoverageDimensionScore[];
  },
): boolean {
  if (req.coverageDimensions && req.coverageDimensions.length > 0) {
    const readiness = computeReadinessScores(req.coverageDimensions);
    return isBelowCoverageGoal({
      coverageCompleteness: readiness.coverageCompleteness,
      ticketReadiness: readiness.ticketReadiness,
    });
  }
  return req.completeness < COVERAGE_GOAL_THRESHOLD;
}

export function inferCoverageFeedbackMode(
  readiness: CoverageGoalReadiness,
): boolean {
  return isBelowCoverageGoal(readiness);
}
